import { useEffect, useState } from 'react'
import { API_URL, fetchOptions } from 'store/fetchUtils'
import { Segment } from 'utils/enums'

const COMPLETE_POSTAL_CODE = /^\d{4}-\d{3}$/

/**
 * Fetches the forwarding model provider suggestions.
 *
 * Runs only for AT accidents once the required initial answers are
 * present. When defaults are forced (eyes/teeth or regional/central postal),
 * only body part + postal code are required and central/ITA are sent as true.
 *
 * Returns { suggestions, setSuggestions, isLoading }. `setSuggestions` is
 * exposed so the caller can clear it (e.g. when body part = "No information").
 */
export const useForwardingSuggestions = ({
	insuranceId,
	accidentType,
	requisitionStateId,
	bodyPart,
	bodyPartNoInfo,
	postalCode,
	postalCodeNoInfo,
	isSerious,
	hasStoppedWorking,
	isOnSickLeave,
	forcedForwardingDefaults,
	postalForcesDefaults,
	contactLocked = false,
	skipSuggestions = false,
}) => {
	const [suggestions, setSuggestions] = useState(null)
	const [isLoading, setIsLoading] = useState(false)

	// Once the victim has been contacted the suggestion is recalculated from the
	// data validated in the contact flow, so the semi-automatic forced defaults
	// (eyes/teeth or regional/central postal) no longer apply — the manually
	// validated central/ITA answers drive the model instead.
	const effectiveForcedDefaults = contactLocked
		? null
		: forcedForwardingDefaults

	useEffect(() => {
		if (requisitionStateId === 2) {
			return
		}

		// Caller explicitly disabled the model (e.g. the injured is not followed
		// in the Trust network) — clear any previous suggestion and never fetch.
		if (skipSuggestions) {
			setSuggestions(null)
			return
		}

		if (accidentType !== Segment.WorkRelated) {
			return
		}

		// Only call the model once the postal code is fully filled (7 digits),
		// otherwise it would fire on every keystroke.
		const isPostalComplete = COMPLETE_POSTAL_CODE.test(postalCode ?? '')

		// Forced defaults (eyes/teeth or regional/central postal) only need body
		// part + postal code; otherwise the model needs body part + postal +
		// central provider. ITA is no longer asked in the initial form — it is
		// always assumed "No". (Business logic)
		const hasRequiredAnswers = effectiveForcedDefaults
			? isPostalComplete
			: isSerious !== undefined && isPostalComplete

		// Any "No information" answer in the initial assessment (body part, postal
		// code, central provider) blocks the model. Once the victim
		// is contacted the data is re-validated in the contact flow, so the frozen
		// initial "No information" flags no longer apply.
		const hasNoInfoAnswer =
			!contactLocked &&
			(bodyPartNoInfo ||
				postalCodeNoInfo ||
				isSerious === 'no_info' ||
				hasStoppedWorking === 'no_info')

		if (!hasNoInfoAnswer && bodyPart !== undefined && hasRequiredAnswers) {
			const fetchSuggestions = async () => {
				try {
					setIsLoading(true)
					const response = await fetch(
						`${API_URL}/api/suggest/processes/suggestions`,
						fetchOptions({
							method: 'POST',
							body: {
								insuranceId: Number(insuranceId),
								bodyPartId: Number(bodyPart),
								postalCode: postalCode,
								prioritary: effectiveForcedDefaults
									? effectiveForcedDefaults.central
									: isSerious,
								// ITA is always "No" in the initial assessment
								// It only carries a real value when manual on contact form
								ita: contactLocked
									? hasStoppedWorking ??
									  isOnSickLeave ??
									  false
									: false,
							},
						})
					)
					if (!response.ok)
						throw new Error('Network response was not ok')
					// The API may return the array under `suggestions` or
					// `providerReferrals`; normalize to providerReferrals.
					const jsonResponse = await response.json()
					setSuggestions({
						...jsonResponse,
						providerReferrals:
							jsonResponse.providerReferrals ??
							jsonResponse.suggestions ??
							[],
					})
				} catch (error) {
					console.error('Error loading suggestions:', error)
					setSuggestions(null)
				} finally {
					setIsLoading(false)
				}
			}
			fetchSuggestions()
		} else {
			//  Drop any previous suggestion so a
			// stale one does not linger on screen.
			setSuggestions(null)
		}
		// eslint-disable-next-line
	}, [
		bodyPart,
		postalCode,
		isSerious,
		hasStoppedWorking,
		isOnSickLeave,
		bodyPartNoInfo,
		postalCodeNoInfo,
		postalForcesDefaults,
		contactLocked,
		skipSuggestions,
	])

	return { suggestions, setSuggestions, isLoading }
}
